import { atom } from "jotai";
import type { RefObject } from "react";

/**
 * A static reference PDF in the resources bucket, opened by the shared viewer.
 * Instances live in `pages/components/referencePdf/referencePdfs.ts`.
 */
export type ReferencePdf = {
  title: string;
  /** Object key in `RESOURCES_BUCKET_NAME`. */
  fileName: string;
  /**
   * OpenSearch `filename` filter for the general-docs search route. When set,
   * the viewer renders its search bar and routes queries to the resolver.
   */
  searchFilename?: string;
};

/**
 * Request to open a reference PDF (AMA Guide / Rating tables) in the one
 * app-wide viewer, at a page or at the first BM25 match for `query`.
 */
export type ReferencePdfOpenRequest = {
  pdf: ReferencePdf;
  page?: number;
  query?: string;
  /** Text to highlight on the landed page — typically the Table/Figure label. */
  highlight?: string;
  /**
   * Sidebar to open beside when the viewer isn't already showing (see
   * `ReferencePdfFloatingViewer.anchorRef`). An open viewer keeps its place.
   */
  anchorRef?: RefObject<HTMLDivElement | null>;
  /**
   * Fires once the request resolves. `found: false` when an unmapped query has
   * no match — the caller shows that feedback (absent: the host toasts).
   * Download failures always toast.
   */
  onResult?: (result: { found: boolean }) => void;
};

/**
 * Cross-surface event bus for the reference-PDF viewer, hosted once by
 * `ReferencePdfViewerHost` in `App`. Callers write a fresh object per
 * click; the host dedups by object identity, so re-clicking the same link
 * re-applies the page/search. Null until the first request.
 */
export const referencePdfOpenRequestAtom = atom<ReferencePdfOpenRequest | null>(
  null,
);

/**
 * What the viewer is showing or fetching, written by the host — lets an Open
 * button show "Viewing" / "Opening..." for its PDF.
 */
export type ReferencePdfViewerStatus = {
  activeFileName: string | null;
  loadingFileName: string | null;
};

export const referencePdfViewerStatusAtom = atom<ReferencePdfViewerStatus>({
  activeFileName: null,
  loadingFileName: null,
});
